import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    SxProps,
    TooltipProps,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import Popup from '../Popup/Popup';
import { ReactComponent as ThreeDotsActionIcon } from '../../../common/images/three-dots-options.svg';
import { COLORS, Tooltip } from '@cellebrite/saas-ui-common';

export interface Action {
    name: string;
    suffix?: any;
    skipClose?: boolean;
    icon?: any;
    onClick?: any;
    disabled?: boolean;
    onClickContent?: any;
}

interface TooltipPropProps {
    content: any;
    icon?: any;
    placement?: TooltipProps['placement'];
}

interface CustomActionsProps {
    tooltip?: TooltipPropProps;
    actions: Array<Action>;
    containerStyles?: SxProps;
    actionTextStyle?: object;
    onToggleActions?: () => void;
    popperStyles?: SxProps;
    prefixElement?: any;
}

const CustomActions: React.FC<CustomActionsProps> = ({
    tooltip,
    actions,
    containerStyles,
    actionTextStyle,
    onToggleActions,
    popperStyles,
    prefixElement,
}) => {
    const [actionContent, setActionContent] = useState(null);
    return (
        <Box sx={{ display: 'flex', right: '18px', ...containerStyles }}>
            {tooltip && (
                <Tooltip
                    content={
                        typeof tooltip.content === 'string' ? (
                            <Typography className="color-mh">{tooltip.content}</Typography>
                        ) : (
                            tooltip.content
                        )
                    }
                    placement={tooltip.placement}
                >
                    <IconButton
                        disableRipple
                        sx={{
                            p: 0,
                            alignItems: 'unset',
                            cursor: 'unset',
                        }}
                    >
                        {tooltip.icon}
                    </IconButton>
                </Tooltip>
            )}
            {prefixElement}
            <Popup
                popperStyles={popperStyles}
                onToggle={() => {
                    onToggleActions?.();
                }}
                actionElement={(handleOpen, anchorReference, open) => (
                    <Box
                        className="button-container"
                        onClick={(e) => {
                            e['actionsClicked'] = true;
                            if (actionContent) {
                                setActionContent(null);
                            }
                            handleOpen();
                        }}
                        ref={anchorReference}
                    >
                        <IconButton className={open ? 'open' : ''} sx={{ width: 24, height: 24 }}>
                            <ThreeDotsActionIcon className="icon" />
                        </IconButton>
                    </Box>
                )}
                contentElement={(handleClose) => {
                    return (
                        <Box sx={{ backgroundColor: COLORS.LIGHT }}>
                            {actionContent || (
                                <List sx={{ m: 1 }}>
                                    {actions.map((action, index) => (
                                        <ListItem
                                            key={index}
                                            disablePadding
                                            onClick={(e) => {
                                                e['actionsClicked'] = true;
                                                if (!action.disabled) {
                                                    if (!action.skipClose) {
                                                        handleClose();
                                                    }
                                                    if (action.onClickContent) {
                                                        setActionContent(action.onClickContent);
                                                    } else if (action.onClick) {
                                                        action.onClick(e);
                                                    }
                                                }
                                            }}
                                            sx={{ pt: index === 0 ? 1 : 0, pb: index === actions.length - 1 ? 1 : 0 }}
                                        >
                                            <ListItemButton
                                                disabled={action.disabled}
                                                sx={{ height: '30px', borderLeft: 'unset !important', p: 0 }}
                                                className="background-color-hover-mho"
                                            >
                                                <ListItemIcon
                                                    className="fill-md"
                                                    sx={{
                                                        justifyContent: 'center',
                                                        minWidth: '40px',
                                                    }}
                                                >
                                                    {action.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    sx={{ pr: 1 }}
                                                    primary={action.name}
                                                    primaryTypographyProps={{ ...actionTextStyle }}
                                                />
                                                {action.suffix ? <Box sx={{ pr: 1 }}>{action.suffix}</Box> : null}
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    );
                }}
            />
        </Box>
    );
};

export default CustomActions;
